// Combined inventory (the PropertyGroup model, still "groups" in code): the derivations the settings
// page needs and the backend cannot pre-compute. Screen vocabulary is locked by
// docs/plans/2026-10-16-combined-inventory.md rule 1: "Combined inventory", "lead listing",
// "following listing", "Same room", "Added room".
//
// The rule here is the same one the rest of the app follows: whatever the SERVER can classify, the
// server classifies. Every candidate on an EXISTING group arrives with `eligible` +
// `blockedReason` already decided (Operator::PropertyGroupsController#candidate_props), and the
// page renders those verbatim.
//
// The create dialog is the one place that cannot work that way: eligibility depends on the lead the
// operator is choosing RIGHT NOW, so nothing exists on the server to have decided it. So the same
// two rules — one clock, one currency — are restated here against the identity strings the server
// sent, in the SAME words, and the mutation rejects the same cases again if this ever drifts. It is
// a comparison of two timezone names and two currency codes, never arithmetic on anything.

export interface GroupCandidate {
  id: number
  name: string
  currency: string | null
  timezone: string | null
  /** Server-decided, on an existing group's candidate list. Absent in the create dialog. */
  eligible?: boolean
  blockedReason?: string | null
}

export interface SeveredRoom {
  roomTypeId: number
  roomTypeName: string
  propertyId: number
  propertyName: string
  /**
   * The canonical path of the hotel this room is IN — which, for a severed cross-property link, is
   * deliberately not the hotel the operator acted on, and never the page's own (the Groups page has
   * no property in scope at all). Backend: Operator::PropertyGroupsController#severed_props.
   */
  propertyBasePath: string
  /**
   * What is being broken: a link to a parent room, or a Same room's link to its twin (combined
   * inventory). Either way the parent fields below name the room it took its price from.
   */
  link?: 'parent' | 'same_room'
  parentRoomTypeName: string | null
  parentPropertyName: string | null
  /**
   * A Same room whose own link to a room on its listing was set aside while it was one: it follows
   * that room again once the Same link is cut. Absent or null otherwise.
   */
  restoredParentRoomTypeName?: string | null
  /**
   * A Same room's offset from its twin goes with the Same link: "Its +10% offset from Double will be
   * removed" in a confirmation, "...was removed" in the report after it. Server-composed in the
   * notice's tense; absent or null without an offset.
   */
  offsetNote?: string | null
  pricingStatusAfter: string | null
  /** The room will be independent with no base rate — so nothing prices or publishes it. */
  willBeUnpriced: boolean
}

export interface GroupMember {
  id: number
  name: string
  /** This member's own canonical path — see SeveredRoom.propertyBasePath for why it is per-row. */
  basePath: string
  isLead: boolean
  roomTypeCount: number
  unitCount: number
  dryRun: boolean
  canDecouple: boolean
  decoupleBlockedReason: string | null
  decouplePreview: { groupDestroyed: boolean; severed: SeveredRoom[] } | null
  /**
   * Combined inventory: a following listing's room mapping at a glance (server-counted and
   * server-worded). Null on the lead, whose rooms are the ones the others copy. Optional because a few
   * test literals predate it.
   */
  mapping?: GroupMemberMapping | null
  /**
   * Combined inventory: a following listing's room mapping page (server-built, §3.20). Null on the lead and
   * for someone who cannot open every listing. Optional for older literals.
   */
  mappingHref?: string | null
  /**
   * Combined inventory: a following listing's Same room offsets at a glance (server-composed), with the
   * way to change them. Null on the lead and on a listing with no Same room. Optional for older literals.
   */
  offsets?: GroupMemberOffsets | null
}

export interface GroupMemberMapping {
  sameRoomCount: number
  addedRoomCount: number
  /** "2 Same rooms · 1 Added room" */
  summary: string
}

/**
 * "Offsets: Double Studio +10%, others none". Offsets are set in one place, the listing's room mapping
 * page (§3.20); `href` lands there, built by the server, and is null when this person cannot open every
 * listing of the combined inventory.
 */
export interface GroupMemberOffsets {
  summary: string
  href: string | null
  /** "Change offset" or "Change offsets". */
  linkLabel: string
}

export interface PropertyGroupRow {
  id: number
  /**
   * The lead listing's name. A combined inventory has no name of its own and the operator is never
   * asked for one (server: PropertyGroup#name), so this always equals `leadPropertyName`.
   */
  name: string
  leadPropertyId: number | null
  leadPropertyName: string | null
  currency: string | null
  timezone: string | null
  hotelCount: number
  roomTypeCount: number
  unitCount: number
  /**
   * What is true now and needs the operator (server-composed, App::CombinedInventoryNotices): a
   * Same-room rate plan pair a structure sync has made unsound, and a following listing with Same rooms
   * that does not publish while the lead does. Optional because a few test literals predate it.
   */
  notices?: string[]
  members: GroupMember[]
  dissolvePreview: { severed: SeveredRoom[] }
  candidates: GroupCandidate[]
}

/**
 * Why this hotel cannot join a group led by `lead` — or null when it can.
 *
 * Wording mirrors Operator::PropertyGroupsController#candidate_blocked_reason exactly, because the
 * create dialog and an existing group's "Add hotel" dialog sit next to each other and must not
 * describe the same refusal two ways.
 */
export function candidateBlockedReason(
  candidate: Pick<GroupCandidate, 'currency' | 'timezone'>,
  lead: Pick<GroupCandidate, 'currency' | 'timezone'> | null,
): string | null {
  if (!lead) return null

  if (candidate.timezone !== lead.timezone) {
    return `On ${candidate.timezone}, but the lead listing is on ${lead.timezone}. Every listing shares one clock.`
  }
  if (candidate.currency !== lead.currency) {
    return `Prices in ${candidate.currency}, but the lead listing prices in ${lead.currency}. Every listing shares one currency.`
  }
  return null
}

/**
 * Candidates split into the ones that can join and the ones that cannot, each blocked one carrying
 * the reason. Prefers a server-decided `blockedReason` when there is one; falls back to deriving it
 * against the chosen lead (the create dialog).
 */
export function partitionCandidates(
  candidates: GroupCandidate[],
  lead: Pick<GroupCandidate, 'id' | 'currency' | 'timezone'> | null,
): { eligible: GroupCandidate[]; blocked: { candidate: GroupCandidate; reason: string }[] } {
  const eligible: GroupCandidate[] = []
  const blocked: { candidate: GroupCandidate; reason: string }[] = []

  for (const candidate of candidates) {
    if (lead && candidate.id === lead.id) continue

    const reason = candidate.blockedReason ?? candidateBlockedReason(candidate, lead)
    if (reason) blocked.push({ candidate, reason })
    else eligible.push(candidate)
  }

  return { eligible, blocked }
}

/** The severed rooms that will actually be left without a price. The subset the warning is about. */
export function unpricedRooms(severed: SeveredRoom[]): SeveredRoom[] {
  return severed.filter((room) => room.willBeUnpriced)
}

export type SeveranceTone = 'none' | 'informational' | 'warning'

export interface SeveranceWarning {
  tone: SeveranceTone
  /** One sentence, sized to the actual consequence. Never a paraphrase of a rule that does not apply. */
  headline: string
  rooms: SeveredRoom[]
  unpriced: SeveredRoom[]
}

/**
 * THE DECOUPLE / DISSOLVE WARNING (locked product rule 4).
 *
 * A room may take its rate from a parent room in another hotel, which only works while both hotels
 * are priced in one pass. Splitting them severs the link and the room becomes independent — and an
 * independent room with no base rate is not priced at all.
 *
 * Three outcomes, deliberately different, because a destructive confirmation that overstates its
 * consequence teaches the operator to ignore it:
 *   * nothing severed          → no warning at all
 *   * severed, all have rates  → informational: they price on their own, nothing goes dark
 *   * severed and unpriced     → the warning, with the exact count
 */
export function severanceWarning(severed: SeveredRoom[]): SeveranceWarning {
  const unpriced = unpricedRooms(severed)

  if (severed.length === 0) {
    return { tone: 'none', headline: '', rooms: [], unpriced }
  }

  if (unpriced.length === 0) {
    return {
      tone: 'informational',
      headline: `${countRooms(severed.length)} will price on their own from their own base rates.`,
      rooms: severed,
      unpriced,
    }
  }

  return {
    tone: 'warning',
    headline: `${countRooms(unpriced.length)} will be left without a price until you set a base rate.`,
    rooms: severed,
    unpriced,
  }
}

function countRooms(n: number): string {
  return n === 1 ? '1 room' : `${n} rooms`
}

/** "2 listings", "1 listing": used in badges and in the consequence sentences. */
export function countListings(n: number): string {
  return n === 1 ? '1 listing' : `${n} listings`
}

/**
 * What a combined inventory card always states: where prices are set, that every listing's bookings
 * count toward the building, and what stays per listing (publishing). Billing is not stated here:
 * the Groups rule 6 sentence ("billed separately") is replaced by combined inventory rule 5, and the
 * billing surfaces carry the new lines.
 */
export function groupConsequences(group: Pick<PropertyGroupRow, 'hotelCount' | 'leadPropertyName'>): string[] {
  // A combined inventory reduced to its lead listing alone has nothing to share bookings with, so
  // only the first and last sentences apply.
  if (group.hotelCount === 1) {
    return [`Prices are set at ${group.leadPropertyName}.`, 'It publishes its own prices to the PMS.']
  }

  return [
    `Prices for these ${countListings(group.hotelCount)} are set once, at ${group.leadPropertyName}.`,
    'Bookings on every listing count toward the building.',
    'Each listing still publishes its own prices to the PMS.',
  ]
}

/**
 * Whether the create form may be submitted, and why not when it may not. Only the lead is asked for:
 * the combined inventory takes its name.
 */
export function createGroupBlocker(leadId: number | null): string | null {
  if (leadId === null) return 'Choose the lead listing.'
  return null
}

// ── The property switcher ────────────────────────────────────────────────────

interface SwitcherProperty {
  id: number
  name: string
  currency?: string
  /** `name` is the combined inventory's, which is its lead listing's (server: PropertyGroup#name). */
  group?: { id: number; name: string; isLead: boolean; leadPropertyId: number | null } | null
}

export interface SwitcherRow<T extends SwitcherProperty = SwitcherProperty> {
  property: T
  /** 0 = a standalone hotel or a group's lead; 1 = a member hanging under its lead. */
  depth: 0 | 1
  /**
   * The combined inventory this hotel belongs to, for a caption: its lead listing's name. Null when it
   * is ungrouped.
   */
  groupName: string | null
  isLead: boolean
  /** Ends the connecting rail on the last member of a group block. */
  lastInGroup: boolean
}

/**
 * The switcher's rows: a group's LEAD with its members nested underneath it, so the operator can see
 * at a glance which hotels are steered as one unit and which one leads. Without that, a member looks
 * like an ordinary hotel whose pricing pages inexplicably redirect elsewhere.
 *
 * WHILE A SEARCH IS ACTIVE THE NESTING IS DROPPED. A member indented under a lead the filter removed
 * would be indented under nothing; so a filtered list is flat, and every grouped row carries its
 * group name as context instead. Searching for a hotel must find that hotel.
 *
 * A member whose lead is absent from the list (an inactive lead is not listed, while its members
 * still are — and are still priced by it) is emitted at the top level with its group named, never
 * dropped.
 */
export function buildSwitcherRows<T extends SwitcherProperty>(properties: T[], query = ''): SwitcherRow<T>[] {
  const q = query.trim().toLowerCase()
  const matching = q === '' ? properties : properties.filter((p) => p.name.toLowerCase().includes(q))

  if (q !== '') {
    return matching.map((property) => ({
      property,
      depth: 0 as const,
      groupName: property.group?.name ?? null,
      isLead: property.group?.isLead ?? false,
      lastInGroup: false,
    }))
  }

  const membersByGroup = new Map<number, T[]>()
  // Which groups have their LEAD in this list. The backend orders properties by NAME, so a member
  // routinely comes before its lead ("Harbour Annex Hotel" sorts above "Harbour Lead Hotel") — a
  // member has to be held back until the lead is reached rather than emitted where it is found.
  const leadPresent = new Set<number>()

  for (const property of properties) {
    const group = property.group
    if (!group) continue
    if (group.isLead) {
      leadPresent.add(group.id)
      continue
    }
    const bucket = membersByGroup.get(group.id)
    if (bucket) bucket.push(property)
    else membersByGroup.set(group.id, [property])
  }

  const rows: SwitcherRow<T>[] = []

  for (const property of properties) {
    const group = property.group

    // A lead: emit it, then everything hanging off it.
    if (group?.isLead) {
      rows.push({ property, depth: 0, groupName: group.name, isLead: true, lastInGroup: false })

      const members = membersByGroup.get(group.id) ?? []
      members.forEach((member, index) => {
        rows.push({
          property: member,
          depth: 1,
          groupName: group.name,
          isLead: false,
          lastInGroup: index === members.length - 1,
        })
      })
      continue
    }

    // A member whose lead IS in the list has already been (or will be) emitted under it.
    if (group && leadPresent.has(group.id)) continue

    // A member whose lead is absent from the list, or a plain standalone hotel.
    rows.push({
      property,
      depth: 0,
      groupName: group ? group.name : null,
      isLead: false,
      lastInGroup: false,
    })
  }

  return rows
}
